<script setup lang="ts">
import { computed, ref } from "vue";
import { Dialog } from "../../../../base-components/Headless";
import Button from "../../../../base-components/Button";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import { usePedidosStore } from "../../../../services/pedidos";
import { useTasasIVAStore } from "../../../../services/tasas";
import { useMensajesStore } from "../../../../services/mensajes";
import { userUserStore } from "../../../../stores/user";
import type { Pedido } from "@fixeduy/restaurantestypes";
import {
  crearFacturaFixedVenta,
  type ApiFixedClienteVenta,
  type ApiFixedVentaRequest
} from "../../../../services/cloudFunctions/fixed/apiFixedVenta";
import { printHTMLContent } from "../../../../utils/helper";
import {
  utilFechaConvertirNumeroString,
  utilFechaGetCurrentTime
} from "../../../../utils/fechaFixedTypes";

interface Props {
  pedido: Pedido;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "cerrar"): void;
}>();

const pedidosStore = usePedidosStore();
const tasasStore = useTasasIVAStore();
const mensajesStore = useMensajesStore();
const userStore = userUserStore();

const sendButtonRef = ref(null);
const cargando = ref(false);
const errorMsg = ref<string>("");

const tipoCfeLabel = computed(() =>
  props.pedido.cfeTipo === "101" ? "e-Ticket" : "e-Factura"
);

const cfeLabel = computed(
  () =>
    `${tipoCfeLabel.value} ${props.pedido.cfeSerie} ${props.pedido.cfeNumero}`
);

const clienteFacturaDesdePedido = (
  pedido: Pedido
): ApiFixedClienteVenta | Record<string, never> => {
  const raw = pedido.cliente;
  if (!raw || typeof raw !== "string") return {};
  try {
    const o = JSON.parse(raw) as unknown;
    if (
      o &&
      typeof o === "object" &&
      typeof (o as ApiFixedClienteVenta).tipoDocumento === "number"
    ) {
      return o as ApiFixedClienteVenta;
    }
  } catch {
    /* ignore */
  }
  return {};
};

const construirPayloadFixedVenta = (
  pedido: Pedido,
  items: Awaited<ReturnType<typeof pedidosStore.getPedidoById>>
): ApiFixedVentaRequest => {
  const fechaPedido = pedido.fechaCierre || pedido.fechaCreacion;
  return {
    apiKey: "JU4L6W22-0P8A-G5F3-PN3Z-438UNRWV9G5J",
    rutComercio: userStore.empresa?.rut.toString() ?? "",
    cliente: clienteFacturaDesdePedido(pedido),
    fecha:
      utilFechaConvertirNumeroString(fechaPedido, false, "/") ??
      utilFechaConvertirNumeroString(utilFechaGetCurrentTime(), false, "/"),
    moneda: "UYU",
    montoBruto: 1,
    lineasVenta: items.map(it => ({
      cantidad: it.cantidad,
      descripcion: it.nombre ?? "",
      descripcionLarga: "",
      rubro: it.rubro,
      tasaIva: tasasStore.getTasaIVAById(it.tasaIvaId)?.cfeDgi ?? "",
      descuentoMonto: it.descuentoMonto ?? 0,
      precioUnitario: it.precioUnitarioConIva ?? 0
    })),
    observaciones: `# ${pedido.numero} | ${pedido.mesaNombre} | ${utilFechaConvertirNumeroString(pedido.fechaCreacion, true, "/")} - ${utilFechaConvertirNumeroString(utilFechaGetCurrentTime(), true, "/")}`,
    creadaDesde: "web"
  };
};

const confirmarFacturacion = async () => {
  if (cargando.value) return;
  cargando.value = true;
  errorMsg.value = "";

  try {
    if (props.pedido.facturado) {
      mensajesStore.mostrarToast(
        "Pedido ya facturado",
        cfeLabel.value,
        mensajesStore.mensajeType.exito
      );
      emit("cerrar");
      return;
    }

    const empresaRut = userStore.empresa?.rut;
    if (!empresaRut) {
      throw new Error("No hay RUT de la empresa configurado.");
    }

    if (!tasasStore.tasas.length) {
      await tasasStore.getTasasFromFirestore();
    }
    const items = await pedidosStore.getPedidoById(props.pedido.id);
    if (!items.length) {
      throw new Error("El pedido no tiene ítems para facturar.");
    }

    const payload = construirPayloadFixedVenta(props.pedido, items);
    const respuesta = await crearFacturaFixedVenta(payload);

    const puedenPersistirCFE =
      respuesta?.idCFE != null &&
      respuesta?.numero != null &&
      respuesta?.serie != null &&
      respuesta?.tipoCFE != null &&
      respuesta?.idVenta != null;

    if (!puedenPersistirCFE) {
      throw new Error(
        "Fixed no devolvió datos completos de CFE para guardar en la base de datos."
      );
    }

    const okPersist = await pedidosStore.updatePedido(props.pedido.id, {
      cfeId: respuesta.idCFE,
      cfeNumero: respuesta.numero,
      cfeSerie: respuesta.serie,
      cfeTipo: respuesta.tipoCFE,
      ventaId: respuesta.idVenta,
      facturado: true,
      devuelto: false
    });

    if (!okPersist) {
      mensajesStore.mostrarToast(
        "Factura Fixed generada",
        "Fixed respondió OK, pero no se pudo actualizar el pedido en la base de datos.",
        mensajesStore.mensajeType.error
      );
      emit("cerrar");
      return;
    }

    mensajesStore.mostrarToast(
      "Pedido facturado",
      "La factura fue enviada a Fixed correctamente.",
      mensajesStore.mensajeType.exito
    );
    mensajesStore.mostrarToast(
      "Factura Fixed enviada",
      "Cargando impresion...",
      mensajesStore.mensajeType.exito
    );
    // await imprimirCFEA4();
    // await imprimirCFETicket();
    await printHTMLContent(respuesta.htmlTicket);
    emit("cerrar");
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.log("Mensaje ", msg);
    errorMsg.value = msg;
    mensajesStore.mostrarToast(
      "Error al facturar",
      msg,
      mensajesStore.mensajeType.error
    );
  } finally {
    cargando.value = false;
  }
};
</script>

<template>
  <Dialog
    :open="true"
    @close="
      () => {
        emit('cerrar');
      }
    "
    :initialFocus="sendButtonRef"
    size="xl"
  >
    <Dialog.Panel
      class="overflow-auto flex flex-col max-w-[100vw] max-h-[85vh]"
    >
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">Facturar pedido</h2>
      </Dialog.Title>

      <Dialog.Description>
        <div class="px-1 py-1">
          <p class="mt-1 text-sm text-slate-700">
            ¿Quiere generar la factura en Fixed para este pedido?
          </p>
          <p v-if="props.pedido.facturado" class="mt-2 text-sm text-amber-600">
            {{ cfeLabel }}
          </p>
        </div>

        <p v-if="errorMsg" class="mt-3 text-sm text-danger" role="alert">
          {{ errorMsg }}
        </p>
      </Dialog.Description>

      <!-------------------- DIALOG FOOTER ------------------------>
      <Dialog.Footer>
        <div v-if="cargando" class="flex justify-center items-center">
          <LoadingIcon icon="oval" class="w-8 h-8" />
          <span class="ml-2 text-sm text-slate-600">Procesando...</span>
        </div>
        <div v-else class="flex text-right">
          <div class="flex-1 text-left">
            <Button
              variant="secondary"
              class="mb-2 mr-1"
              @click="emit('cerrar')"
            >
              Cancelar
            </Button>
          </div>
          <div class="flex-1">
            <Button
              ref="sendButtonRef"
              variant="primary"
              class="mb-2 mr-1"
              @click="confirmarFacturacion"
            >
              Facturar
            </Button>
          </div>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
